import { useState } from "react";
import { Stream, StudentGroup } from "../../model/stream";
import { useToast } from "../../../../context/toast/toast-provider";
import { addStudentGroup, deleteStudentGroup, updateStudentGroup } from "../../service/student-group-service";
import BaseForm from "../../../../component/form/base-form";
import { FieldDefinition } from "../../../../component/form/field-definition";

const groupFieldDefinitions: FieldDefinition[] = [
    {
        id: "name",
        name: "Name",
        type: "text",
        required: true,
        disabled: false,
        min: 2,
        max: 63
    },
    {
        id: "groupEmail",
        name: "Group email",
        type: "email",
        required: false,
        disabled: false,
    },
    {
        id: "active",
        name: "Active",
        type: "checkbox",
        required: false,
        disabled: false,
    }
];

export default function CohortStreamGroupHandler({
    stream,
    group,
    onSaved,
    onDeleted,
    onCancel,
}: {
    stream: Stream,
    group: StudentGroup | undefined,
    onSaved: () => void,
    onDeleted: () => void,
    onCancel: () => void,
}) {
    const newModel = group === undefined;
    const [model, setModel] = useState<StudentGroup>(group ?? {
        name: "",
        groupEmail: "",
        streamId: stream.id!,
        active: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const toast = useToast();

    const handleSubmit = (group: StudentGroup) => {
        setSubmitting(true);
        const payload = { ...group, streamId: stream.id! };

        if (newModel) {
            addStudentGroup(stream.id!, payload).then(() => {
                toast.addToast(`Group "${group.name}" created`, "success");
                setSubmitting(false);
                onSaved();
            }).catch(() => {
                toast.addToast(`Unable to create group`, "error");
                setSubmitting(false);
            });
        } else {
            updateStudentGroup(stream.id!, payload).then(() => {
                toast.addToast(`Group "${group.name}" updated`, "success");
                setSubmitting(false);
                onSaved();
            }).catch(() => {
                toast.addToast(`Unable to update group`, "error");
                setSubmitting(false);
            });
        }
    }

    const handleDelete = () => {
        setDeleting(true);
        deleteStudentGroup(stream.id!, group!.id!).then(() => {
            toast.addToast(`Group "${group!.name}" deleted`, "success");
            setDeleting(false);
            onDeleted();
        }).catch(() => {
            toast.addToast(`Unable to delete group`, "error");
            setDeleting(false);
        });
    }

    return (
        <BaseForm
            name={newModel ? "Add Group" : `Edit Group: ${group!.name}`}
            model={model}
            setModel={setModel}
            fields={groupFieldDefinitions}
            submitButtonText={newModel ? "Create" : "Update"}
            submitEntity={handleSubmit}
            submitting={submitting}
            deleteButtonText={newModel ? undefined : "Delete"}
            deleteConfirmationText={`Are you sure you want to delete group "${group?.name}"?`}
            deleteEntity={newModel ? undefined : handleDelete}
            deleting={deleting}
            cancelButtonText="Back"
            cancel={onCancel}
        />
    );
}
